/**
 * CoachingNoteEditor — Create/edit coaching notes with category tags.
 */

import { useState } from 'react';
import { View, Text, TextInput, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';

const CATEGORIES = ['General', 'Form Check', 'Programming', 'Recovery', 'Nutrition'] as const;
type Category = typeof CATEGORIES[number];

export interface CoachingNote {
  id?: string;
  content: string;
  category: Category;
  created_at?: string;
}

interface Props {
  clientId: string;
  onSave: (note: { content: string; category: Category }) => Promise<void>;
  existingNote?: CoachingNote | null;
  onCancel?: () => void;
}

export default function CoachingNoteEditor({ clientId, onSave, existingNote, onCancel }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;

  const [content, setContent] = useState(existingNote?.content ?? '');
  const [category, setCategory] = useState<Category>(existingNote?.category ?? 'General');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await onSave({ content: content.trim(), category });
      if (!existingNote) {
        setContent('');
        setCategory('General');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: c.surface, borderColor: c.border }]}>
      <TextInput
        style={[styles.input, { color: c.text, borderColor: c.border, backgroundColor: c.background }]}
        placeholder="Add a coaching note..."
        placeholderTextColor={c.textTertiary}
        value={content}
        onChangeText={setContent}
        multiline
        numberOfLines={4}
        textAlignVertical="top"
      />

      <View style={styles.tagsRow}>
        {CATEGORIES.map((cat) => (
          <TouchableOpacity
            key={cat}
            style={[
              styles.tag,
              {
                backgroundColor: category === cat ? c.accent + '22' : c.background,
                borderColor: category === cat ? c.accent : c.border,
              },
            ]}
            onPress={() => setCategory(cat)}
          >
            <Text
              style={[
                styles.tagText,
                { color: category === cat ? c.accent : c.textSecondary },
              ]}
            >
              {cat}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      <View style={styles.actions}>
        {onCancel && (
          <TouchableOpacity onPress={onCancel} style={[styles.btn, { borderColor: c.border }]}>
            <Text style={{ color: c.textSecondary, fontWeight: '600', fontSize: 14 }}>Cancel</Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity
          style={[styles.btn, styles.saveBtn, { backgroundColor: c.accent, opacity: content.trim() ? 1 : 0.5 }]}
          onPress={handleSave}
          disabled={!content.trim() || saving}
        >
          {saving ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <Text style={styles.saveBtnText}>{existingNote ? 'Update' : 'Save'}</Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 12,
    gap: 10,
  },
  input: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 10,
    minHeight: 80,
    fontSize: 14,
  },
  tagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  tag: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  tagText: {
    fontSize: 12,
    fontWeight: '600',
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
  },
  btn: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 16,
    paddingVertical: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveBtn: {
    borderWidth: 0,
  },
  saveBtnText: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 14,
  },
});
